import { useEffect, useState } from "react"
import GooglePlacesAutocomplete from "react-google-places-autocomplete"
import styled from "styled-components"
import { HelperText } from "ui/base/helperText/HelperText"
import { SearchIcon } from "ui/icons/inputIcons/SearchIcon"
import { defaultThemeColors, fonts } from "ui/theme"

const options = {
  default: {
    componentRestrictions: {
      country: ["us"],
    },
  },
  city: {
    types: ["(cities)"],
    componentRestrictions: {
      country: ["us"],
    },
  },
  region: {
    types: ["(regions)"],
    componentRestrictions: {
      country: ["us"],
    },
  },
}
export const AutoCompleteInputVariant = ({
  onChange,
  address,
  placeholder,
  isError,
  errorText,
  placeholderColor = "placeholder",
  bgColor,
  borderColor,
  rounded,
  hasIcon,
  optionType = "default",
  ...restProps
}) => {
  const [inputValue, setInputValue] = useState(address?.label)
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const [focus, setFocus] = useState(false)

  useEffect(() => {
    if (inputValue !== address?.label) {
      setInputValue(address?.label || "")
    }
  }, [address?.label])

  const handleInputChange = (newValue, { action }) => {
    if (action === "input-change") {
      setFocus(true)
      setInputValue(newValue)
      if (newValue) {
        setMenuIsOpen(true)
      } else {
        handleChange(null)
        setMenuIsOpen(false)
      }
    }
  }

  const handleChange = (option) => {
    onChange(option)
    setMenuIsOpen(false)
    setInputValue(option ? option.label : "")
  }

  return (
    <InputWrapper bgColor={bgColor}>
      <InputContainer
        isError={isError}
        focus={focus}
        placeholderColor={placeholderColor}
        borderColor={borderColor}
        rounded={rounded}
        bgColor={bgColor}>
        <GooglePlacesAutocomplete
          apiKey={process.env.REACT_APP_GOOGLE_AUTO_COMPLETE}
          apiOptions={{ language: "en", region: "us" }}
          autocompletionRequest={options[optionType]}
          selectProps={{
            value: address,
            onChange: handleChange,
            inputValue,
            placeholder,
            onInputChange: handleInputChange,
            menuIsOpen: menuIsOpen,
            onMenuClose: () => setMenuIsOpen(false),
            className: "auto-complete-container",
            classNamePrefix: "auto-complete",
            styles: {
              control: () => null,
              SelectContainer: () => null,
              Input: () => null,
            },
            onBlur: () => {
              setFocus(false)
            },
            onFocus: () => {
              setFocus(true)
            },
            ...restProps,
            components: {
              Input: Input,
              IndicatorsContainer: () => null,
              SingleValue: () => null,
            },
          }}
          debounce={200}
        />
        {hasIcon ? (
          <InputIcon
            isError={isError}
            hasValue={!!inputValue}
            focus={focus}>
            <SearchIcon />
          </InputIcon>
        ) : null}
      </InputContainer>

      {isError ? (
        <HelperText
          color='pink'
          size='small'>
          {errorText}
        </HelperText>
      ) : null}
    </InputWrapper>
  )
}

const Input = ({ cx, selectProps, ...props }) => {
  return (
    <InputStyled
      {...props}
      isHidden={false}
      data-testid={selectProps?.["data-testid"]}
    />
  )
}

export const InputWrapper = styled.div`
  width: 100%;
  display: grid;
  gap: 1rem;
  background-color: ${({ bgColor = "inputBgNeutral" }) =>
    defaultThemeColors[bgColor]};
  border-radius: 5px;
`

export const InputContainer = styled.label`
  display: flex;
  align-items: center;
  gap: 1rem;
  height: 43px;
  font-size: var(--auto-complete-font-size, ${fonts.sizes.regular});
  border-width: 2px;
  border-style: solid;
  border-color: ${({ isError, borderColor }) =>
    isError
      ? defaultThemeColors.pink
      : defaultThemeColors[borderColor ? borderColor : "transparent"]};
  border-radius: ${({ rounded }) => (rounded ? "99px" : "5px")};
  padding-inline: 1rem;
  background-color: white;

  & .auto-complete-container,
  .auto-complete__value-container,
  .auto-complete__control {
    width: 100%;
    height: 100%;
    padding: 0px;
    margin: 0px;
  }

  & .auto-complete__placeholder {
    color: ${({ placeholderColor }) => defaultThemeColors[placeholderColor]};
    font-size: inherit;
    font-weight: 400;
    white-space: nowrap;
  }

  & .auto-complete__single-value {
    font-size: inherit;
    color: ${({ color = "txt" }) => defaultThemeColors[color]};
  }
`

export const InputIcon = styled.label`
  height: 100%;
  width: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ focus }) => (focus ? "0.6" : "0.3")};
  color: ${({ isError, hasValue }) =>
    isError ? defaultThemeColors.pink : defaultThemeColors.txt};
`

export const InputStyled = styled.input`
  background-color: white;
  font-size: var(--auto-complete-font-size, ${fonts.sizes.regular});
  font-family: ${fonts.families.primary};
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  color: ${defaultThemeColors.txt};
`
